import { ThemedText } from "@/components/themed-text";
import { Label } from "@/components/ui/label";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import Ionicons from "@react-native-vector-icons/ionicons";
import React, { useCallback, useState, type CSSProperties } from "react";
import { Platform, StyleSheet, TouchableOpacity, View } from "react-native";
import { HelperText } from "react-native-paper";
import { calendarIndicatorImage } from "./date-picker";

function toLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

interface DateTimePickerInputProps {
  label: string;
  value?: Date;
  onChange: (date: Date) => void;
  error?: string;
  showPickUpNowButton?: boolean;
  onPickUpNow?: () => void;
  minimumDate?: Date;
}

export const DateTimePickerInput: React.FC<DateTimePickerInputProps> = ({
  label,
  value,
  onChange,
  error,
  showPickUpNowButton = false,
  onPickUpNow,
  minimumDate = new Date(),
}) => {
  const { theme } = useAuth();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempDate, setTempDate] = useState<Date>(value || new Date());

  const openNativePicker = useCallback((input: HTMLInputElement | null) => {
    if (Platform.OS !== "web" || !input) {
      return;
    }
    input.focus();
    try {
      input.showPicker();
    } catch (error) {
      console.log("Picker not supported or blocked", error);
    }
  }, []);

  const handleOpenDatePicker = () => {
    setTempDate(value || new Date());
    onChange(value || new Date());
    setShowDatePicker(true);
  };

  const handleConfirmDate = () => {
    onChange(tempDate);
    setShowDatePicker(false);
  };

  const handleCancelDate = () => {
    setShowDatePicker(false);
  };

  return (
    <View style={styles.inputGroup}>
      <View style={styles.sectionHeader}>
        <Label label={label} />
        {showPickUpNowButton && onPickUpNow && (
          <TouchableOpacity
            style={[
              styles.pickupNowButton,
              { backgroundColor: theme.background, borderColor: theme.border },
            ]}
            onPress={onPickUpNow}
          >
            <Ionicons name="calendar" size={12} color={BrandColors.primary} />
            <ThemedText style={styles.pickupNowButtonText}>Today</ThemedText>
          </TouchableOpacity>
        )}
      </View>

      {!showDatePicker && (
        <TouchableOpacity
          style={[
            styles.dateSelectionCard,
            { backgroundColor: theme.background },
            error && styles.inputError,
          ]}
          onPress={handleOpenDatePicker}
        >
          <View
            style={[
              styles.dateIconContainer,
              { backgroundColor: theme.background },
            ]}
          >
            <Ionicons name="calendar" size={24} color={BrandColors.primary} />
          </View>
          <View style={styles.dateTextContainer}>
            <Label label="Scheduled Date & Time" />
            <ThemedText style={styles.dateValue}>
              {value
                ? value.toLocaleString(undefined, {
                    weekday: "short",
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "Select date & time"}
            </ThemedText>
          </View>
          <Ionicons
            name="chevron-forward"
            size={20}
            color={BrandColors.mediumGray}
          />
        </TouchableOpacity>
      )}

      {error && (
        <HelperText type="error" visible={!!error}>
          {error}
        </HelperText>
      )}

      {showDatePicker && (
        <>
          <style
            dangerouslySetInnerHTML={{
              __html: `
              .date-picker-input {
                border-style: solid;
                border-color: ${theme.border};
                outline: none;
              }
              .date-picker-input:focus {
                border-color: ${theme.primary};
                outline: 1.5px solid ${theme.primary};
              }
              .date-picker-input::-webkit-calendar-picker-indicator {
                background-image: url("${calendarIndicatorImage(theme.text)}");
                background-repeat: no-repeat;
                background-position: center;
                background-size: 18px 18px;
                cursor: pointer;
              }
            `,
            }}
          />
          <input
            className="date-picker-input"
            type="datetime-local"
            value={toLocalInputValue(tempDate)}
            ref={openNativePicker}
            onChange={(e: any) => {
              if (e.target.value) {
                setTempDate(new Date(e.target.value));
              }
            }}
            onBlur={() => {
              setShowDatePicker(false);
            }}
            onKeyUp={(e: any) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleConfirmDate();
              }
            }}
            min={minimumDate ? toLocalInputValue(minimumDate) : undefined}
            style={
              StyleSheet.flatten([
                styles.dateInput,
                {
                  color: theme.text,
                  backgroundColor: theme.background,
                },
              ]) as CSSProperties
            }
          />
          <View style={styles.datePickerActions}>
            <TouchableOpacity
              style={[styles.datePickerButton, styles.datePickerButtonCancel]}
              onPress={handleCancelDate}
            >
              <ThemedText style={styles.datePickerButtonText}>
                Cancel
              </ThemedText>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.datePickerButton, styles.datePickerButtonConfirm]}
              onPress={handleConfirmDate}
            >
              <ThemedText
                style={[
                  styles.datePickerButtonText,
                  styles.datePickerButtonTextConfirm,
                ]}
              >
                OK
              </ThemedText>
            </TouchableOpacity>
          </View>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  inputGroup: {
    gap: 4,
  },
  pickupNowButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BrandColors.primary,
  },
  pickupNowButtonText: {
    fontSize: 12,
    color: BrandColors.primary,
    fontWeight: "800",
  },
  datePickerActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
    marginTop: 12,
  },
  datePickerButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
  },
  datePickerButtonCancel: {
    backgroundColor: "#f5f5f5",
  },
  datePickerButtonConfirm: {
    backgroundColor: BrandColors.primary,
  },
  datePickerButtonText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#666",
  },
  datePickerButtonTextConfirm: {
    color: "#fff",
  },
  dateSelectionCard: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 0,
    borderRadius: 20,
    padding: 16,
    marginTop: 8,
    gap: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 12,
    elevation: 4,
  },
  dateIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  dateTextContainer: {
    flex: 1,
    gap: 4,
  },
  dateValue: {
    fontSize: 16,
    fontWeight: "600",
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  inputError: {
    borderColor: BrandColors.error,
    borderWidth: 1,
  },
  dateInput: {
    fontSize: 16,
    borderWidth: 0.5,
    borderRadius: 8,
    padding: 12,
  },
});
